/* eslint-disable @typescript-eslint/no-non-null-assertion --
 * The seed builder uses constrained `arr[0]!` / `.find(...)!` assertions on
 * collections it just constructed. The assertions are correct by construction;
 * the alternative is verbose nullish-coalescing that adds no runtime safety.
 * Localised disable; the rule remains on everywhere else.
 */

/**
 * Deterministic top-down seed graph.
 *
 * Build order (each layer references only earlier layers):
 *
 *   operators → strategies → broker accounts → clients →
 *   mandates → eligibility rules → cohorts → templates →
 *   rollouts → assignments → subscriptions →
 *   trades → pnl → signals → risk violations →
 *   reconciliation events → remediation items → audit events.
 *
 * Every cross-reference is asserted in `./invariants.ts`, which is run as a
 * Vitest invariants test so a seed bug becomes a test failure rather than a
 * runtime surprise.
 *
 * Reference date pinned at 2026-05-12 (today per the system context). All
 * timestamps are anchored relative to it.
 */

import type {
  AssetClass,
  Client,
  ClientTier,
  KycRecord,
  Operator,
  OperatorRole,
  RiskRule,
  StrategyMeta,
} from '@/types/domain';
import type {
  BrokerAccount,
  PnLSnapshot,
  ReconciliationEvent,
  RiskViolation,
  Signal,
  Strategy,
  Subscription,
  Trade,
  User,
} from '@/types/api';
import type {
  Assignment,
  AutoPauseRule,
  Cohort,
  CohortRule,
  EligibilityRule,
  PhaseMetrics,
  RemediationItem,
  Rollout,
  RolloutPhase,
  StrategyTemplate,
} from '@/types/assignment';
import type { AuditEvent } from '@/types/events';
import type { INR, ISODateTime, LotMult, PercentPoints, QtyMult, Ratio, USD } from '@/types/money';
import {
  _unsafeInr,
  _unsafeIsoDateTime,
  _unsafeUsd,
} from '@/types/money';

import { DEFAULT_SEED, makeRng, type Rng } from './rng';

/* ============================================================================
 * Reference data and pinned values
 * ============================================================================ */

export const SEED_REFERENCE_DATE = '2026-05-12T03:30:00.000Z';
const REFERENCE_MS = new Date(SEED_REFERENCE_DATE).getTime();

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const MIN_MS = 60 * 1000;

const offsetIso = (offsetMs: number): ISODateTime =>
  _unsafeIsoDateTime(new Date(REFERENCE_MS + offsetMs).toISOString());

/* ============================================================================
 * Static catalogues — used by generators
 * ============================================================================ */

const FIRST_NAMES = [
  'Aarav', 'Vihaan', 'Aditya', 'Sai', 'Arjun', 'Reyansh', 'Mohammed', 'Ayaan', 'Krishna', 'Ishaan',
  'Anaya', 'Aadhya', 'Aaradhya', 'Saanvi', 'Diya', 'Myra', 'Anika', 'Pari', 'Riya', 'Aanya',
];
const LAST_NAMES = [
  'Sharma', 'Verma', 'Iyer', 'Reddy', 'Khan', 'Singh', 'Kumar', 'Mehta', 'Patel', 'Joshi',
  'Nair', 'Pillai', 'Banerjee', 'Chatterjee', 'Bose', 'Rao', 'Krishnan', 'Subramaniam', 'Gupta', 'Agarwal',
];

const NSE_INDICES = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'] as const;
const NSE_STOCKS = [
  'RELIANCE', 'TCS', 'HDFCBANK', 'INFY', 'ICICIBANK', 'SBIN', 'AXISBANK', 'KOTAKBANK',
  'ITC', 'LT', 'WIPRO', 'HCLTECH', 'MARUTI', 'TATAMOTORS', 'SUNPHARMA', 'ASIANPAINT',
] as const;
const US_TICKERS = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'NVDA', 'TSLA', 'JPM'] as const;
const CRYPTO_PERPS = ['BTC-PERP', 'ETH-PERP', 'SOL-PERP'] as const;

const STRATEGY_FAMILIES = ['vega-short', 'theta-decay', 'momentum-breakout', 'mean-reversion', 'pair-trading', 'index-arbitrage'] as const;

/* ============================================================================
 * Helpers
 * ============================================================================ */

const sequentialId = (prefix: string, index: number, pad = 4): string =>
  `${prefix}-${String(index).padStart(pad, '0')}`;

const inrAmt = (n: number): INR => _unsafeInr(Math.round(n * 100) / 100);
const usdAmt = (n: number): USD => _unsafeUsd(Math.round(n * 100) / 100);

/* ============================================================================
 * Operators
 * ============================================================================ */

const ROLE_DESKS: Record<OperatorRole, string> = {
  admin: 'Engineering',
  trader: 'Trading',
  manual_trader: 'Trading',
  risk: 'Risk',
  ops: 'Operations',
  compliance: 'Compliance',
  readonly: 'Audit',
};

function buildOperators(rng: Rng): Operator[] {
  const seed: Array<{ role: OperatorRole; display: string }> = [
    { role: 'admin', display: 'Aarav Sharma' },
    { role: 'trader', display: 'Diya Iyer' },
    { role: 'trader', display: 'Krishna Mehta' },
    { role: 'risk', display: 'Anika Reddy' },
    { role: 'ops', display: 'Vihaan Pillai' },
    { role: 'ops', display: 'Saanvi Banerjee' },
    { role: 'compliance', display: 'Ayaan Khan' },
    { role: 'compliance', display: 'Riya Subramaniam' },
    { role: 'readonly', display: 'Aanya Gupta' },
  ];

  return seed.map((s, i): Operator => {
    const id = sequentialId('op', i + 1, 3);
    const created = offsetIso(-DAY_MS * (180 + rng.int(0, 90)));
    return {
      id,
      email: `${s.display.toLowerCase().replace(' ', '.')}@invasset.example`,
      display_name: s.display,
      role: s.role,
      status: 'active',
      four_eyes_eligible: s.role !== 'readonly',
      desk: ROLE_DESKS[s.role],
      created_at: created,
    };
  });
}

/* ============================================================================
 * Strategies
 * ============================================================================ */

function buildStrategies(rng: Rng, operators: Operator[]): {
  meta: StrategyMeta[];
  wire: Strategy[];
} {
  const traders = operators.filter((o) => o.role === 'trader' || o.role === 'admin');
  const meta: StrategyMeta[] = [];

  const assetByFamily: Record<(typeof STRATEGY_FAMILIES)[number], AssetClass> = {
    'vega-short': 'equity_fno',
    'theta-decay': 'equity_fno',
    'momentum-breakout': 'equity_cash',
    'mean-reversion': 'equity_cash',
    'pair-trading': 'equity_cash',
    'index-arbitrage': 'equity_fno',
  };

  let strategyIdx = 1;
  for (const family of STRATEGY_FAMILIES) {
    const variantCount = family === 'vega-short' || family === 'theta-decay' ? 5 : 4;
    for (let v = 0; v < variantCount; v++) {
      const id = sequentialId('strat', strategyIdx, 3);
      const codeName = `${family.replace(/-/g, '_')}_${['nf', 'bnf', 'fnf', 'idx', 'broad'][v] ?? `v${v}`}`;
      const assetClass = assetByFamily[family];
      meta.push({
        id,
        code_name: codeName,
        display_name: `${family
          .split('-')
          .map((w) => w[0]?.toUpperCase() + w.slice(1))
          .join(' ')} · ${['NF', 'BNF', 'FNF', 'IDX', 'Broad'][v] ?? `V${v}`}`,
        family,
        description: `${family} variant ${v + 1}. Auto-generated description for mock data.`,
        version: `1.${v}.${rng.int(0, 12)}`,
        asset_class: assetClass,
        risk_class: rng.pick(['low', 'medium', 'high'] as const),
        capacity_inr: inrAmt(rng.int(50, 250) * 1_00_00_000), // 50 Cr to 250 Cr
        deployed_aum_inr: inrAmt(rng.int(10, 80) * 1_00_00_000), // backfilled in invariants pass
        default_sizing: {
          mode: 'percent_nav',
          lots_multiplier: _unsafeFromNumberAsLotMult(1.0),
          fixed_qty_multiplier: _unsafeFromNumberAsQtyMult(1.0),
          capital_inr: inrAmt(0),
          capital_usd: usdAmt(0),
          pct_nav: 0.05 as Ratio,
          max_qty: 0,
        },
        owner_operator_id: traders.length > 0 ? rng.pick(traders).id : null,
        runtime_status: rng.chance(0.85) ? 'running' : rng.pick(['stopped', 'failed'] as const),
        tags: rng.sample(['fno', 'india', 'intraday', 'overnight', 'low-vol', 'high-vol'], rng.int(1, 3)),
        typical_lot_size: assetClass === 'equity_fno' ? 25 : 1,
      });
      strategyIdx++;
    }
  }

  const wire: Strategy[] = meta.map((m) => ({
    id: m.id,
    code_name: m.code_name,
    version: m.version,
    params: {
      stop_loss_pct: rng.int(2, 8),
      take_profit_pct: rng.int(4, 16),
      entry_window_minutes: rng.int(15, 90),
    },
    status: m.runtime_status === 'running' ? 'active' : 'paused',
  }));

  return { meta, wire };
}

// LotMult / QtyMult brands accept any finite number; the `_unsafe` constructors are typed.
const _unsafeFromNumberAsLotMult = (n: number): LotMult => n as unknown as LotMult;
const _unsafeFromNumberAsQtyMult = (n: number): QtyMult => n as unknown as QtyMult;

/* ============================================================================
 * Clients (institutional) + their KYC + AUM + corresponding backend Users
 * ============================================================================ */

const TIER_AUM_BANDS: Record<ClientTier, { lo: number; hi: number; weight: number }> = {
  retail: { lo: 50 * 1_00_000, hi: 50 * 1_00_000 * 10, weight: 30 },   // 50L–5Cr
  hni: { lo: 5 * 1_00_00_000, hi: 25 * 1_00_00_000, weight: 30 },        // 5–25 Cr
  uhni: { lo: 25 * 1_00_00_000, hi: 100 * 1_00_00_000, weight: 25 },     // 25–100 Cr
  prop: { lo: 50 * 1_00_00_000, hi: 200 * 1_00_00_000, weight: 10 },     // 50–200 Cr
  fund: { lo: 100 * 1_00_00_000, hi: 400 * 1_00_00_000, weight: 5 },     // 100–400 Cr
};

function buildClients(rng: Rng, operators: Operator[]): {
  clients: Client[];
  kyc: KycRecord[];
  backendUsers: User[];
} {
  const rms = operators.filter((o) => o.role === 'trader' || o.role === 'ops');
  const tiers: ClientTier[] = ['retail', 'hni', 'uhni', 'prop', 'fund'];
  const tierWeights = tiers.flatMap((t) => Array<ClientTier>(TIER_AUM_BANDS[t].weight).fill(t));

  const clients: Client[] = [];
  const kyc: KycRecord[] = [];
  const backendUsers: User[] = [];

  for (let i = 0; i < 50; i++) {
    const tier = rng.pick(tierWeights);
    const band = TIER_AUM_BANDS[tier];
    const aum = inrAmt(rng.int(band.lo, band.hi));
    const onboardedAt = offsetIso(-DAY_MS * rng.int(60, 1000));
    const displayName = `${rng.pick(FIRST_NAMES)} ${rng.pick(LAST_NAMES)}`;
    const id = sequentialId('cli', i + 1, 4);
    const backendUserId = sequentialId('user', i + 1, 4);

    const kycStatus = rng.chance(0.92) ? 'verified' : rng.chance(0.5) ? 'expired' : 'in_progress';
    const baseCcy: 'INR' | 'USD' = tier === 'fund' && rng.chance(0.5) ? 'USD' : 'INR';

    clients.push({
      id,
      backend_user_id: backendUserId,
      display_name: displayName,
      legal_name: `${displayName.toUpperCase()} (${tier === 'prop' ? 'Prop' : 'Indl'})`,
      tier,
      risk_profile: tier === 'retail' ? 'conservative' : rng.pick(['conservative', 'moderate', 'aggressive'] as const),
      current_aum_inr: aum,
      base_currency: baseCcy,
      kyc_status: kycStatus,
      mandate_id: sequentialId('mandate', i + 1, 4),
      mandate_revision: rng.chance(0.2) ? 2 : 1,
      onboarded_at: onboardedAt,
      primary_rm_id: rms.length > 0 ? rng.pick(rms).id : null,
      status: kycStatus === 'expired' ? 'paused' : 'active',
      tags: rng.sample(['vip', 'newsletter', 'high-touch', 'family-office', 'algo-savvy'], rng.int(0, 3)),
      country: baseCcy === 'USD' ? 'AE' : 'IN',
      notes: rng.chance(0.3) ? `Mock note for ${displayName}` : null,
      created_at: onboardedAt,
      updated_at: offsetIso(-DAY_MS * rng.int(1, 30)),
    });

    kyc.push({
      id: sequentialId('kyc', i + 1, 4),
      client_id: id,
      status: kycStatus,
      documents: [
        { kind: 'pan', last4: String(rng.int(1000, 9999)), verified_at: kycStatus === 'verified' ? onboardedAt : null, expires_at: null },
        { kind: 'aadhaar', last4: String(rng.int(1000, 9999)), verified_at: onboardedAt, expires_at: null },
        ...(tier === 'fund' || tier === 'prop'
          ? [{ kind: 'gstin' as const, last4: String(rng.int(1000, 9999)), verified_at: onboardedAt, expires_at: null }]
          : []),
      ],
      next_review_at: offsetIso(DAY_MS * rng.int(30, 365)),
    });

    backendUsers.push({
      id: backendUserId,
      email: clients[i]!.legal_name.toLowerCase().replace(/[^a-z0-9]/g, '') + '@invasset.example',
      display_name: displayName,
      status: 'active',
      created_at: onboardedAt,
    });
  }

  return { clients, kyc, backendUsers };
}

/* ============================================================================
 * Broker accounts — 1–2 per client; brokers vary by client country
 * ============================================================================ */

const BROKER_TYPES_INDIA = ['kite', 'upstox'] as const;
const BROKER_TYPES_ABROAD = ['ibkr'] as const;

function buildBrokerAccounts(rng: Rng, clients: Client[]): BrokerAccount[] {
  const out: BrokerAccount[] = [];
  for (const c of clients) {
    const count = c.tier === 'fund' || c.tier === 'prop' ? rng.int(2, 4) : rng.int(1, 3);
    const brokers = c.country === 'IN' ? BROKER_TYPES_INDIA : BROKER_TYPES_ABROAD;
    for (let i = 0; i < count; i++) {
      const broker_type = rng.pick(brokers);
      const id = sequentialId('brk', out.length + 1, 4);
      out.push({
        id,
        client_id: c.backend_user_id ?? sequentialId('user', 0, 4),
        broker_type,
        display_name: `${c.display_name.split(' ')[0]}'s ${broker_type.toUpperCase()}`,
        position_mode: broker_type === 'kite' ? 'netting' : (rng.chance(0.7) ? 'netting' : 'hedging'),
        base_currency: c.country === 'IN' ? 'INR' : 'USD',
        timezone: c.country === 'IN' ? 'Asia/Kolkata' : 'America/New_York',
        status: rng.chance(0.95) ? 'active' : 'paused',
        created_at: c.onboarded_at,
      });
    }
  }
  return out;
}

/* ============================================================================
 * Eligibility rules + cohorts
 * ============================================================================ */

function buildEligibilityRules(rng: Rng, operators: Operator[]): EligibilityRule[] {
  const compliance = operators.find((o) => o.role === 'compliance');
  const author = compliance?.id ?? operators[0]!.id;
  const now = offsetIso(0);

  const baseRules: Array<{ predicate: CohortRule; reason: string; enforcement: EligibilityRule['enforcement'] }> = [
    {
      predicate: { op: 'PREDICATE', predicate: { kind: 'kyc_status_eq', status: 'verified' } },
      reason: 'KYC must be verified.',
      enforcement: 'block',
    },
    {
      predicate: { op: 'PREDICATE', predicate: { kind: 'mandate_allows', flag: 'equity_fno' } },
      reason: 'Mandate must permit F&O for this strategy.',
      enforcement: 'block',
    },
    {
      predicate: {
        op: 'NOT',
        node: { op: 'PREDICATE', predicate: { kind: 'current_dd_gt', threshold_pct: 8 as PercentPoints } },
      },
      reason: 'Current intraday drawdown is above 8%.',
      enforcement: 'warn',
    },
    {
      predicate: { op: 'PREDICATE', predicate: { kind: 'aum_gt', threshold_inr: inrAmt(50_00_000) } },
      reason: 'Minimum AUM ₹50L required.',
      enforcement: 'require_override',
    },
  ];

  return baseRules.map((r, i): EligibilityRule => ({
    id: sequentialId('elig', i + 1, 3),
    strategy_id: i === 0 ? null : null,
    predicate: r.predicate,
    enforcement: r.enforcement,
    reason_template: r.reason,
    active: true,
    created_by: author,
    created_at: offsetIso(-DAY_MS * rng.int(30, 200)),
    updated_at: now,
    current_blocking_count: rng.int(0, 6),
    mock: true,
  }));
}

function buildCohorts(rng: Rng, operators: Operator[], clients: Client[]): Cohort[] {
  const ops = operators.find((o) => o.role === 'ops') ?? operators[0]!;
  const now = offsetIso(0);

  const allByTier: Record<ClientTier, Client[]> = { retail: [], hni: [], uhni: [], prop: [], fund: [] };
  for (const c of clients) {
    allByTier[c.tier].push(c);
  }

  const cohorts: Cohort[] = [];

  const ruleHniPlus: CohortRule = {
    op: 'AND',
    nodes: [
      { op: 'PREDICATE', predicate: { kind: 'tier_in', tiers: ['hni', 'uhni'] } },
      { op: 'PREDICATE', predicate: { kind: 'kyc_status_eq', status: 'verified' } },
    ],
  };
  cohorts.push({
    id: 'cohort-001',
    name: 'HNI+ verified',
    description: 'HNI and UHNI clients with verified KYC.',
    membership_mode: 'rule_based',
    rule: ruleHniPlus,
    manual_member_ids: null,
    refresh_cadence: 'hourly',
    last_refreshed_at: offsetIso(-HOUR_MS * 2),
    member_count: 0,
    drift_warning: null,
    current_member_ids: [...allByTier.hni, ...allByTier.uhni].filter((c) => c.kyc_status === 'verified').map((c) => c.id),
    created_by: ops.id,
    created_at: offsetIso(-DAY_MS * 90),
    updated_at: now,
    tags: ['default', 'hni'],
    mock: true,
  });

  cohorts.push({
    id: 'cohort-002',
    name: 'F&O permitted',
    description: 'Any client whose mandate allows equity F&O.',
    membership_mode: 'rule_based',
    rule: {
      op: 'PREDICATE',
      predicate: { kind: 'mandate_allows', flag: 'equity_fno' },
    },
    manual_member_ids: null,
    refresh_cadence: 'daily',
    last_refreshed_at: offsetIso(-HOUR_MS * 6),
    member_count: 0,
    drift_warning: null,
    current_member_ids: clients.filter((_c, i) => i % 4 !== 0).map((cl) => cl.id), // ~75% of clients
    created_by: ops.id,
    created_at: offsetIso(-DAY_MS * 60),
    updated_at: now,
    tags: ['default'],
    mock: true,
  });

  cohorts.push({
    id: 'cohort-003',
    name: 'Pilot opt-in',
    description: 'Hand-picked early-pilot members.',
    membership_mode: 'manual',
    rule: null,
    manual_member_ids: rng.sample(clients, 8).map((c) => c.id),
    refresh_cadence: 'manual',
    last_refreshed_at: offsetIso(-DAY_MS * 4),
    member_count: 8,
    drift_warning: null,
    current_member_ids: [],
    created_by: ops.id,
    created_at: offsetIso(-DAY_MS * 14),
    updated_at: now,
    tags: ['pilot'],
    mock: true,
  });
  cohorts[2]!.current_member_ids = cohorts[2]!.manual_member_ids ?? [];

  cohorts.push({
    id: 'cohort-004',
    name: 'UHNI aggressive',
    description: 'UHNI with aggressive risk profile.',
    membership_mode: 'rule_based',
    rule: {
      op: 'AND',
      nodes: [
        { op: 'PREDICATE', predicate: { kind: 'tier_eq', tier: 'uhni' } },
        { op: 'PREDICATE', predicate: { kind: 'risk_profile_eq', profile: 'aggressive' } },
      ],
    },
    manual_member_ids: null,
    refresh_cadence: 'daily',
    last_refreshed_at: offsetIso(-HOUR_MS * 8),
    member_count: 0,
    drift_warning: { pinned_at: offsetIso(-DAY_MS * 30), added_since: 2, removed_since: 1, severity: 'info' },
    current_member_ids: allByTier.uhni.filter((c) => c.risk_profile === 'aggressive').map((c) => c.id),
    created_by: ops.id,
    created_at: offsetIso(-DAY_MS * 45),
    updated_at: now,
    tags: ['aggressive', 'uhni'],
    mock: true,
  });

  cohorts.push({
    id: 'cohort-005',
    name: 'Prop book',
    description: 'Proprietary trading book accounts.',
    membership_mode: 'rule_based',
    rule: { op: 'PREDICATE', predicate: { kind: 'tier_eq', tier: 'prop' } },
    manual_member_ids: null,
    refresh_cadence: 'realtime',
    last_refreshed_at: offsetIso(-MIN_MS * 5),
    member_count: 0,
    drift_warning: null,
    current_member_ids: allByTier.prop.map((c) => c.id),
    created_by: ops.id,
    created_at: offsetIso(-DAY_MS * 120),
    updated_at: now,
    tags: ['prop'],
    mock: true,
  });

  cohorts.push({
    id: 'cohort-006',
    name: 'Fund vehicles',
    description: 'PMS / AIF wrappers.',
    membership_mode: 'rule_based',
    rule: { op: 'PREDICATE', predicate: { kind: 'tier_eq', tier: 'fund' } },
    manual_member_ids: null,
    refresh_cadence: 'daily',
    last_refreshed_at: offsetIso(-DAY_MS * 1),
    member_count: 0,
    drift_warning: null,
    current_member_ids: allByTier.fund.map((c) => c.id),
    created_by: ops.id,
    created_at: offsetIso(-DAY_MS * 150),
    updated_at: now,
    tags: ['fund'],
    mock: true,
  });

  cohorts.push({
    id: 'cohort-007',
    name: 'Recently onboarded',
    description: 'Onboarded in the last 90 days.',
    membership_mode: 'rule_based',
    rule: { op: 'PREDICATE', predicate: { kind: 'onboarded_after', date: offsetIso(-DAY_MS * 90) } },
    manual_member_ids: null,
    refresh_cadence: 'daily',
    last_refreshed_at: offsetIso(-DAY_MS * 1),
    member_count: 0,
    drift_warning: null,
    current_member_ids: clients
      .filter((cl) => new Date(cl.onboarded_at).getTime() > REFERENCE_MS - DAY_MS * 90)
      .map((cl) => cl.id),
    created_by: ops.id,
    created_at: offsetIso(-DAY_MS * 30),
    updated_at: now,
    tags: ['onboarding'],
    mock: true,
  });

  cohorts.push({
    id: 'cohort-008',
    name: 'Hybrid HNI',
    description: 'Rule-based HNI plus manual additions.',
    membership_mode: 'hybrid',
    rule: { op: 'PREDICATE', predicate: { kind: 'tier_eq', tier: 'hni' } },
    manual_member_ids: rng.sample(allByTier.uhni, 3).map((c) => c.id),
    refresh_cadence: 'hourly',
    last_refreshed_at: offsetIso(-HOUR_MS * 3),
    member_count: 0,
    drift_warning: null,
    current_member_ids: [],
    created_by: ops.id,
    created_at: offsetIso(-DAY_MS * 21),
    updated_at: now,
    tags: ['hni', 'hybrid'],
    mock: true,
  });
  cohorts[7]!.current_member_ids = [
    ...allByTier.hni.map((c) => c.id),
    ...(cohorts[7]!.manual_member_ids ?? []),
  ];

  // Finalise member_count on every cohort.
  for (const c of cohorts) {
    c.member_count = c.current_member_ids.length;
  }
  return cohorts;
}

/* ============================================================================
 * Templates
 * ============================================================================ */

function buildTemplates(rng: Rng, operators: Operator[], strategies: StrategyMeta[]): StrategyTemplate[] {
  const ops = operators.find((o) => o.role === 'ops') ?? operators[0]!;
  const now = offsetIso(0);

  const defs: Array<{ name: string; tiers: ClientTier[]; size: number; auto: boolean }> = [
    { name: 'Retail Conservative Default', tiers: ['retail'], size: 3, auto: true },
    { name: 'HNI Balanced Default', tiers: ['hni'], size: 5, auto: true },
    { name: 'UHNI Aggressive Default', tiers: ['uhni'], size: 7, auto: false },
    { name: 'Prop Book Default', tiers: ['prop'], size: 10, auto: false },
    { name: 'Fund Vehicle Default', tiers: ['fund'], size: 12, auto: false },
    { name: 'HNI + UHNI F&O', tiers: ['hni', 'uhni'], size: 6, auto: false },
    { name: 'Crypto-allowed Aggressive', tiers: ['uhni', 'prop'], size: 4, auto: false },
    { name: 'Onboarding Pilot', tiers: ['retail', 'hni'], size: 2, auto: false },
  ];

  return defs.map((d, i): StrategyTemplate => ({
    id: sequentialId('tmpl', i + 1, 3),
    name: d.name,
    applies_to_tiers: d.tiers,
    items: rng.sample(strategies, d.size).map((s) => ({
      strategy_id: s.id,
      default_sizing: s.default_sizing,
      default_rollout_id: null,
    })),
    auto_apply_on_onboarding: d.auto,
    created_by: ops.id,
    created_at: offsetIso(-DAY_MS * rng.int(30, 200)),
    updated_at: now,
    mock: true,
  }));
}

/* ============================================================================
 * Rollouts — one in each phase (PILOT, RAMPING-25, RAMPING-50, FULL/COMPLETED)
 * ============================================================================ */

const DEFAULT_AUTO_PAUSE: AutoPauseRule[] = [
  { kind: 'realized_dd_gt', threshold: 0.06, window: 'phase', severity: 'pause', expression: null },
  { kind: 'slippage_bps_gt', threshold: 50, window: 'rolling_24h', severity: 'warn', expression: null },
  { kind: 'error_rate_gt', threshold: 0.02, window: 'phase', severity: 'pause', expression: null },
  { kind: 'variance_from_backtest_sigma_gt', threshold: 3, window: 'phase', severity: 'pause', expression: null },
];

function makeDefaultPhases(rng: Rng): RolloutPhase[] {
  const phases: RolloutPhase[] = [
    { index: 0, name: 'Pilot 5%', target_pct: 5 as PercentPoints, target_member_ids: null, min_duration_hours: 72, starts_at: null, ended_at: null, status: 'PENDING', metrics_snapshot: null },
    { index: 1, name: 'Ramp 25%', target_pct: 25 as PercentPoints, target_member_ids: null, min_duration_hours: 96, starts_at: null, ended_at: null, status: 'PENDING', metrics_snapshot: null },
    { index: 2, name: 'Ramp 50%', target_pct: 50 as PercentPoints, target_member_ids: null, min_duration_hours: 96, starts_at: null, ended_at: null, status: 'PENDING', metrics_snapshot: null },
    { index: 3, name: 'Full 100%', target_pct: 100 as PercentPoints, target_member_ids: null, min_duration_hours: 0, starts_at: null, ended_at: null, status: 'PENDING', metrics_snapshot: null },
  ];
  void rng;
  return phases;
}

function buildRollouts(rng: Rng, operators: Operator[], strategies: StrategyMeta[], cohorts: Cohort[]): Rollout[] {
  const ops1 = operators.find((o) => o.role === 'ops') ?? operators[0]!;
  const ops2 = operators.filter((o) => o.role === 'ops')[1] ?? ops1;

  const rollouts: Rollout[] = [];
  const candidateStrategies = strategies.slice(0, 4);
  const phaseTargets: Array<{ phase: 0 | 1 | 2 | 3; status: Rollout['status'] }> = [
    { phase: 0, status: 'RUNNING' },
    { phase: 1, status: 'RUNNING' },
    { phase: 2, status: 'PAUSED' },
    { phase: 3, status: 'COMPLETED' },
  ];

  for (let i = 0; i < 4; i++) {
    const strategy = candidateStrategies[i] ?? strategies[i % strategies.length]!;
    const cohort = cohorts[(i % cohorts.length)] ?? cohorts[0]!;
    const target = phaseTargets[i]!;
    const phases = makeDefaultPhases(rng);
    const startMs = -DAY_MS * (10 + i * 4);

    // Mark all phases up to current as completed; current phase RUNNING / PAUSED.
    for (let p = 0; p <= target.phase; p++) {
      const ph = phases[p]!;
      ph.starts_at = offsetIso(startMs + p * DAY_MS * 4);
      if (p < target.phase) {
        ph.status = 'PROMOTED';
        ph.ended_at = offsetIso(startMs + (p + 1) * DAY_MS * 4);
        ph.metrics_snapshot = synthPhaseMetrics(rng, true);
      } else {
        ph.status = target.status === 'PAUSED' ? 'PAUSED' : target.status === 'COMPLETED' ? 'PROMOTED' : 'RUNNING';
        ph.metrics_snapshot = synthPhaseMetrics(rng, target.status === 'COMPLETED');
        if (target.status === 'COMPLETED') {
          ph.ended_at = offsetIso(startMs + (p + 1) * DAY_MS * 4);
        }
      }
    }

    rollouts.push({
      id: sequentialId('rollout', i + 1, 3),
      strategy_id: strategy.id,
      cohort_id: cohort.id,
      name: `${strategy.display_name} → ${cohort.name}`,
      status: target.status,
      phases,
      current_phase_index: target.phase,
      started_at: offsetIso(startMs),
      completed_at: target.status === 'COMPLETED' ? offsetIso(startMs + 4 * DAY_MS * 4) : null,
      paused_reason: target.status === 'PAUSED' ? 'Auto-pause: realized_dd_pct breached 6% threshold (current 6.4%).' : null,
      auto_pause_rules: DEFAULT_AUTO_PAUSE,
      shadow_mode: i === 0,
      shadow_compare_window_days: i === 0 ? 5 : null,
      created_by: ops1.id,
      approved_by: ops2.id,
      version: 1,
      audit_trail_ids: [],
      mock: true,
    });
  }
  return rollouts;
}

function synthPhaseMetrics(rng: Rng, completed: boolean): PhaseMetrics {
  return {
    members_enrolled: rng.int(2, 20),
    members_active: rng.int(2, 18),
    realized_dd_pct: (rng.random() * 7) as PercentPoints,
    error_count: rng.int(0, 4),
    slippage_bps_avg: rng.gauss(15, 8),
    fill_rate: (0.92 + rng.random() * 0.07) as Ratio,
    variance_from_backtest_sigma: rng.gauss(0.5, 0.8),
    rejection_rate: (rng.random() * 0.03) as Ratio,
    pnl_attribution_inr: inrAmt(rng.gauss(2_50_000, 1_50_000)),
    completed_at: completed ? offsetIso(-DAY_MS * rng.int(1, 30)) : null,
  };
}

/* ============================================================================
 * Assignments — 200 records, mix of manual / template / cohort origins
 * ============================================================================ */

const ASSIGNMENT_STATE_WEIGHTS: Array<{ state: Assignment['state']; w: number }> = [
  { state: 'ACTIVE', w: 60 },
  { state: 'PILOT', w: 8 },
  { state: 'RAMPING', w: 10 },
  { state: 'SCHEDULED', w: 5 },
  { state: 'PAUSED_BY_OPERATOR', w: 5 },
  { state: 'PAUSED_BY_RISK', w: 2 },
  { state: 'PAUSED_BY_MANDATE', w: 3 },
  { state: 'BLOCKED', w: 2 },
  { state: 'REMEDIATION', w: 2 },
  { state: 'WINDING_DOWN', w: 1 },
  { state: 'TERMINATED', w: 2 },
];

function buildAssignments(
  rng: Rng,
  operators: Operator[],
  strategies: StrategyMeta[],
  clients: Client[],
  cohorts: Cohort[],
  templates: StrategyTemplate[],
  rollouts: Rollout[],
): Assignment[] {
  const opsForCreate = operators.filter((o) => o.role === 'trader' || o.role === 'ops');
  const opsForApprove = operators.filter((o) => o.role === 'ops' || o.role === 'admin');
  const stateBag = ASSIGNMENT_STATE_WEIGHTS.flatMap((sw) => Array<Assignment['state']>(sw.w).fill(sw.state));

  const out: Assignment[] = [];

  for (let i = 0; i < 200; i++) {
    const strategy = rng.pick(strategies);
    const client = rng.pick(clients);
    const state = rng.pick(stateBag);
    const origin = rng.pick(['manual', 'template', 'cohort'] as const);

    const id = sequentialId('asn', i + 1, 4);
    const created = offsetIso(-DAY_MS * rng.int(2, 400));
    const validFrom = offsetIso(-DAY_MS * rng.int(1, 200));

    const eligibilityPassed = state !== 'BLOCKED';
    const mandateCompliant = state !== 'PAUSED_BY_MANDATE' && state !== 'REMEDIATION';

    out.push({
      id,
      strategy_id: strategy.id,
      client_id: client.id,
      origin,
      template_id: origin === 'template' ? rng.pick(templates).id : null,
      cohort_id: origin === 'cohort' ? rng.pick(cohorts).id : null,
      rollout_id: state === 'PILOT' || state === 'RAMPING' ? rng.pick(rollouts).id : null,
      state,
      sizing_override: rng.chance(0.25)
        ? { pct_nav: (0.03 + rng.random() * 0.04) as Ratio }
        : null,
      eligibility: {
        passed: eligibilityPassed,
        violations: eligibilityPassed
          ? []
          : [
              {
                rule_id: 'elig-001',
                reason: 'KYC must be verified.',
                actual: 'expired',
                expected: 'verified',
              },
            ],
        last_checked_at: offsetIso(-HOUR_MS * rng.int(1, 12)),
      },
      mandate: {
        compliant: mandateCompliant,
        violations: mandateCompliant
          ? []
          : [
              {
                field: 'max_strategy_exposure_pct',
                required: '≤ 10%',
                actual: '12.4%',
              },
            ],
        last_checked_at: offsetIso(-HOUR_MS * rng.int(1, 12)),
        remediation_item_id: state === 'REMEDIATION' ? sequentialId('rem', (i % 6) + 1, 3) : null,
      },
      subscriptions: [], // filled in by subscriptions stage
      valid_from: validFrom,
      valid_to: state === 'TERMINATED' ? offsetIso(-DAY_MS * rng.int(1, 30)) : null,
      created_by: rng.pick(opsForCreate).id,
      approved_by: state === 'DRAFT' || state === 'PENDING_APPROVAL' ? null : rng.pick(opsForApprove).id,
      paused_reason: state.startsWith('PAUSED_')
        ? state === 'PAUSED_BY_OPERATOR'
          ? 'Operator pause — pre-event flatten'
          : state === 'PAUSED_BY_RISK'
            ? 'Risk: per-client gross exposure 11.2% > 10%'
            : 'Mandate: KYC expired'
        : null,
      created_at: created,
      updated_at: offsetIso(-HOUR_MS * rng.int(0, 24)),
      version: rng.int(1, 6),
      audit_trail_ids: [],
      notes: null,
      mock: true,
    });
  }
  return out;
}

/* ============================================================================
 * Subscriptions — one per (assignment × broker_account-for-client)
 * ============================================================================ */

function buildSubscriptions(
  rng: Rng,
  assignments: Assignment[],
  clients: Client[],
  brokerAccounts: BrokerAccount[],
): Subscription[] {
  const baByUser = new Map<string, BrokerAccount[]>();
  for (const ba of brokerAccounts) {
    const list = baByUser.get(ba.client_id) ?? [];
    list.push(ba);
    baByUser.set(ba.client_id, list);
  }
  const clientByCid = new Map(clients.map((c) => [c.id, c] as const));

  const out: Subscription[] = [];
  for (const a of assignments) {
    if (a.state === 'DRAFT' || a.state === 'PENDING_APPROVAL' || a.state === 'BLOCKED') {
      continue;
    }
    const client = clientByCid.get(a.client_id);
    if (client === undefined || client.backend_user_id === null) {
      continue;
    }
    const accounts = baByUser.get(client.backend_user_id) ?? [];
    if (accounts.length === 0) {
      continue;
    }
    // Pick up to all of the client's broker accounts.
    const picks = rng.sample(accounts, rng.int(1, accounts.length + 1));
    for (const ba of picks) {
      const id = sequentialId('sub', out.length + 1, 5);
      const active = a.state === 'ACTIVE' || a.state === 'PILOT' || a.state === 'RAMPING';
      out.push({
        id,
        client_id: ba.client_id,
        broker_account_id: ba.id,
        strategy_id: a.strategy_id,
        subscribed_at: a.valid_from,
        sizing_config: {
          lots_multiplier: 1.0,
          fixed_qty_multiplier: 1.0,
          capital: Number(client.current_aum_inr) * 0.05,
          max_qty: 0,
        },
        active,
        paused_reason: active ? null : a.paused_reason,
      });
      a.subscriptions.push(id);
    }
  }
  return out;
}

/* ============================================================================
 * Trades / PnL / Signals — small but non-empty
 * ============================================================================ */

function buildTrades(rng: Rng, strategies: StrategyMeta[], subs: Subscription[]): Trade[] {
  const out: Trade[] = [];
  const symbolPool: string[] = [];
  for (const s of strategies) {
    if (s.asset_class === 'equity_fno') {
      symbolPool.push(...NSE_INDICES, ...NSE_STOCKS.slice(0, 6));
    } else if (s.asset_class === 'equity_cash') {
      symbolPool.push(...NSE_STOCKS);
    } else if (s.asset_class === 'us_equity') {
      symbolPool.push(...US_TICKERS);
    } else if (s.asset_class === 'crypto_perps') {
      symbolPool.push(...CRYPTO_PERPS);
    }
  }

  const tradeCount = Math.min(subs.length * 3, 1000);
  for (let i = 0; i < tradeCount; i++) {
    const sub = subs[i % subs.length]!;
    const symbol = rng.pick(symbolPool);
    const side: 'buy' | 'sell' = rng.chance(0.55) ? 'buy' : 'sell';
    const qty = rng.int(25, 500);
    const entryTime = offsetIso(-DAY_MS * rng.random() * 30);
    const isOpen = rng.chance(0.18);
    const entryPrice = 100 + rng.gauss(50, 25);
    const exitPrice = isOpen ? null : entryPrice * (1 + rng.gauss(0, 0.015));
    const realizedPnl = isOpen ? 0 : ((exitPrice ?? 0) - entryPrice) * qty * (side === 'buy' ? 1 : -1);

    out.push({
      id: sequentialId('trade', i + 1, 5),
      symbol,
      side,
      quantity: qty,
      entry_time: entryTime,
      entry_price: Math.round(entryPrice * 100) / 100,
      exit_time: isOpen ? null : offsetIso(new Date(entryTime).getTime() - REFERENCE_MS + HOUR_MS * rng.int(1, 8)),
      exit_price: exitPrice === null ? null : Math.round(exitPrice * 100) / 100,
      is_open: isOpen,
      realized_pnl: Math.round(realizedPnl * 100) / 100,
      entry_signal: sequentialId('sig', i + 1, 5),
      exit_signal: isOpen ? '' : sequentialId('sig', i + 1, 5) + '-exit',
    });
    void sub; // currently the wire shape doesn't carry sub_id; backend ask #X.
  }
  return out;
}

function buildPnl(rng: Rng, strategies: StrategyMeta[]): PnLSnapshot[] {
  const out: PnLSnapshot[] = [];
  const top = strategies.slice(0, 6);
  let runningTotal = 0;
  for (let d = 60; d >= 0; d--) {
    for (const s of top) {
      const realized = rng.gauss(35_000, 70_000);
      const unrealized = rng.gauss(5_000, 25_000);
      runningTotal += realized;
      out.push({
        time: offsetIso(-DAY_MS * d),
        realized: Math.round(realized * 100) / 100,
        unrealized: Math.round(unrealized * 100) / 100,
        total: Math.round((realized + unrealized) * 100) / 100,
      });
      void s;
    }
  }
  void runningTotal;
  return out;
}

function buildSignals(rng: Rng, strategies: StrategyMeta[]): Signal[] {
  const out: Signal[] = [];
  for (let i = 0; i < 200; i++) {
    const strategy = rng.pick(strategies);
    const symbol = strategy.asset_class === 'equity_fno' ? rng.pick(NSE_INDICES) : rng.pick(NSE_STOCKS);
    out.push({
      signal_id: sequentialId('sig', i + 1, 5),
      strategy_id: strategy.id,
      emitted_at: offsetIso(-MIN_MS * rng.int(1, 60 * 24 * 7)),
      reason: rng.pick([
        'Volatility regime change detected',
        'Mean reversion threshold breached',
        'Momentum continuation confirmed',
        'Volume spike + price break',
        'Pair spread reverted to mean',
      ]),
      target_state: {
        legs: [
          {
            symbol,
            side: rng.chance(0.5) ? 'buy' : 'sell',
            size_unit: rng.int(1, 6),
            sizing_mode: 'lots',
            leg_id: null,
            stop_loss: { type: 'percent', value: rng.int(2, 8), trail_by: null },
            take_profit: { type: 'percent', value: rng.int(4, 16) },
          },
        ],
      },
      indicator_snapshot: { iv_rank: rng.int(20, 80), atr: Math.round(rng.gauss(50, 20) * 100) / 100 },
    });
  }
  return out;
}

/* ============================================================================
 * Risk violations + recon events
 * ============================================================================ */

function buildRiskViolations(rng: Rng): RiskViolation[] {
  const out: RiskViolation[] = [];
  const reasons = [
    'per_symbol_exposure_pct_nav > 7%',
    'per_strategy_daily_loss_inr > ₹2 L',
    'top_n_concentration_pct > 35%',
    'time_of_day_no_entries_after 14:55 IST',
    'banned_symbols includes ADANIENT',
  ];
  for (let i = 0; i < 25; i++) {
    out.push({
      id: i + 1,
      rule_id: sequentialId('rrule', rng.int(1, 8), 3),
      symbol: rng.pick(NSE_STOCKS),
      side: rng.chance(0.5) ? 'buy' : 'sell',
      quantity: rng.int(50, 500),
      reason: rng.pick(reasons),
      at: offsetIso(-HOUR_MS * rng.int(1, 24 * 14)),
    });
  }
  return out;
}

function buildReconEvents(rng: Rng): ReconciliationEvent[] {
  const types: ReconciliationEvent['type'][] = ['MISSING_ON_BROKER', 'UNKNOWN_ON_BROKER', 'QTY_MISMATCH'];
  const out: ReconciliationEvent[] = [];
  for (let i = 0; i < 12; i++) {
    const type = rng.pick(types);
    const dbQty = rng.int(0, 500);
    const brokerQty = type === 'QTY_MISMATCH' ? dbQty + rng.int(-50, 50) : type === 'MISSING_ON_BROKER' ? 0 : dbQty;
    out.push({
      id: sequentialId('recon', i + 1, 4),
      type,
      symbol: rng.pick([...NSE_STOCKS, ...NSE_INDICES]),
      db_qty: dbQty,
      broker_qty: brokerQty,
      resolved: rng.chance(0.5),
      at: offsetIso(-HOUR_MS * rng.int(1, 24 * 7)),
    });
  }
  return out;
}

/* ============================================================================
 * Remediation items — at least one per "PAUSED_BY_MANDATE" or REMEDIATION assignment
 * ============================================================================ */

function buildRemediationItems(rng: Rng, assignments: Assignment[], operators: Operator[]): RemediationItem[] {
  const ops = operators.find((o) => o.role === 'ops') ?? operators[0]!;
  const candidates = assignments.filter(
    (a) => a.state === 'PAUSED_BY_MANDATE' || a.state === 'REMEDIATION',
  );
  const picks = candidates.slice(0, 6);
  const out: RemediationItem[] = [];

  picks.forEach((a, i) => {
    const triggered = offsetIso(-HOUR_MS * rng.int(2, 36));
    const sla = offsetIso(new Date(triggered).getTime() - REFERENCE_MS + 24 * HOUR_MS);
    const status = i < 4 ? 'OPEN' : i === 4 ? 'IN_PROGRESS' : 'ESCALATED';
    out.push({
      id: sequentialId('rem', i + 1, 3),
      assignment_id: a.id,
      client_id: a.client_id,
      strategy_id: a.strategy_id,
      trigger: rng.pick(['mandate_changed', 'kyc_expired', 'risk_profile_changed', 'aum_threshold_crossed'] as const),
      triggered_at: triggered,
      sla_deadline: sla,
      severity: rng.pick(['high', 'critical', 'medium'] as const),
      open_position_value_inr: inrAmt(rng.int(5, 80) * 1_00_000),
      status,
      assigned_to: status === 'OPEN' ? null : ops.id,
      resolution: null,
      resolution_reason: null,
      resolution_approved_by: null,
      resolved_at: null,
      notes: status === 'IN_PROGRESS' ? [
        { operator_id: ops.id, at: offsetIso(-HOUR_MS * 1), text: 'Investigating exposure; client outreach scheduled.' },
      ] : [],
      mock: true,
    });
    // Wire the assignment back to the remediation item id.
    a.mandate.remediation_item_id = out[out.length - 1]!.id;
  });
  return out;
}

/* ============================================================================
 * Audit events — historical seed entries (so /admin/audit isn't empty)
 * ============================================================================ */

function buildAuditEvents(rng: Rng, operators: Operator[], assignments: Assignment[]): AuditEvent[] {
  const out: AuditEvent[] = [];
  const sample = rng.sample(assignments, 40);
  let seq = 0;
  for (const a of sample) {
    const actor = rng.pick(operators);
    out.push({
      id: sequentialId('audit', seq + 1, 6),
      seq,
      at: offsetIso(-HOUR_MS * rng.int(1, 24 * 30)),
      actor_id: actor.id,
      actor_role: actor.role,
      approved_by: a.approved_by,
      approved_by_role: a.approved_by ? operators.find((o) => o.id === a.approved_by)?.role ?? null : null,
      category: 'assignment',
      severity: 'info',
      reason: 'seeded mock event',
      tags: ['seed'],
      mock: true,
      payload_hash: '0'.repeat(64),
      type: 'ASSIGNMENT_CREATED',
      assignment_id: a.id,
      strategy_id: a.strategy_id,
      client_id: a.client_id,
      via: a.origin,
    });
    seq++;
  }
  return out;
}

/* ============================================================================
 * Risk rule taxonomy (mock CRUD)
 * ============================================================================ */

function buildRiskRules(rng: Rng, operators: Operator[]): RiskRule[] {
  const author = operators.find((o) => o.role === 'risk')?.id ?? operators[0]!.id;
  const now = offsetIso(0);
  const kinds: RiskRule['kind'][] = [
    'per_symbol_exposure_pct_nav',
    'per_strategy_daily_loss_inr',
    'per_broker_margin_util_pct',
    'drawdown_intraday_pct',
    'drawdown_daily_pct',
    'order_rate_orders_per_sec',
    'top_n_concentration_pct',
    'strategy_concentration_across_clients_pct_aum',
  ];
  return kinds.map((kind, i): RiskRule => ({
    id: sequentialId('rrule', i + 1, 3),
    kind,
    threshold: kind.endsWith('_inr') ? rng.int(1, 5) * 100_000 : rng.int(2, 30),
    scope: {},
    severity: rng.pick(['warn', 'block'] as const),
    enabled: true,
    notes: '',
    created_by: author,
    created_at: offsetIso(-DAY_MS * rng.int(30, 180)),
    updated_at: now,
  }));
}

/* ============================================================================
 * Final shape + builder + memoised getter
 * ============================================================================ */

export interface Seed {
  generatedAt: ISODateTime;
  seedNumber: number;

  operators: Operator[];
  strategies: StrategyMeta[];
  strategiesWire: Strategy[];
  brokerAccounts: BrokerAccount[];
  clients: Client[];
  kyc: KycRecord[];
  backendUsers: User[];
  eligibilityRules: EligibilityRule[];
  cohorts: Cohort[];
  templates: StrategyTemplate[];
  rollouts: Rollout[];
  assignments: Assignment[];
  subscriptions: Subscription[];
  trades: Trade[];
  pnl: PnLSnapshot[];
  signals: Signal[];
  riskRules: RiskRule[];
  riskViolations: RiskViolation[];
  reconciliationEvents: ReconciliationEvent[];
  remediationItems: RemediationItem[];
  auditEvents: AuditEvent[];
}

export const buildSeed = (seedNumber: number = DEFAULT_SEED): Seed => {
  const rng = makeRng(seedNumber);

  const operators = buildOperators(rng);
  const { meta: strategies, wire: strategiesWire } = buildStrategies(rng, operators);
  const { clients, kyc, backendUsers } = buildClients(rng, operators);
  const brokerAccounts = buildBrokerAccounts(rng, clients);
  const eligibilityRules = buildEligibilityRules(rng, operators);
  const cohorts = buildCohorts(rng, operators, clients);
  const templates = buildTemplates(rng, operators, strategies);
  const rollouts = buildRollouts(rng, operators, strategies, cohorts);
  const assignments = buildAssignments(rng, operators, strategies, clients, cohorts, templates, rollouts);
  const subscriptions = buildSubscriptions(rng, assignments, clients, brokerAccounts);
  const trades = buildTrades(rng, strategies, subscriptions);
  const pnl = buildPnl(rng, strategies);
  const signals = buildSignals(rng, strategies);
  const riskRules = buildRiskRules(rng, operators);
  const riskViolations = buildRiskViolations(rng);
  const reconciliationEvents = buildReconEvents(rng);
  const remediationItems = buildRemediationItems(rng, assignments, operators);
  const auditEvents = buildAuditEvents(rng, operators, assignments);

  return {
    generatedAt: offsetIso(0),
    seedNumber,
    operators,
    strategies,
    strategiesWire,
    brokerAccounts,
    clients,
    kyc,
    backendUsers,
    eligibilityRules,
    cohorts,
    templates,
    rollouts,
    assignments,
    subscriptions,
    trades,
    pnl,
    signals,
    riskRules,
    riskViolations,
    reconciliationEvents,
    remediationItems,
    auditEvents,
  };
};

let cached: Seed | null = null;

/** Memoised seed — built once per process. Use `resetSeed()` in tests. */
export const getSeed = (): Seed => {
  if (cached === null) {
    cached = buildSeed();
  }
  return cached;
};

export const resetSeed = (seedNumber: number = DEFAULT_SEED): Seed => {
  cached = buildSeed(seedNumber);
  return cached;
};
